import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/sections/Navigation';
import Footer from '@/sections/Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              KMG ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our software platform, services, and website (collectively, the "Service"). Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              By accessing or using our Service, you consent to the collection, use, and disclosure of your information in accordance with this Privacy Policy. We reserve the right to modify this Privacy Policy at any time, and such modifications shall be effective immediately upon posting the updated Privacy Policy on this page.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">2.1 Information You Provide to Us</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Register for an account or use our Service</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Contact us for customer support or inquiries</li>
              <li>Participate in surveys, promotions, or other interactive features</li>
              <li>Submit feedback, comments, or other user-generated content</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-4">
              This information may include:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Name, email address, phone number, and other contact information</li>
              <li>Company name, job title, and business information</li>
              <li>Payment and billing information (processed through secure third-party payment processors)</li>
              <li>Account credentials and authentication information</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">2.2 Information Automatically Collected</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you access or use our Service, we automatically collect certain information about your device and usage patterns, including:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Device information (IP address, browser type, operating system, device identifiers)</li>
              <li>Usage data (pages visited, features used, time spent, clickstream data)</li>
              <li>Log files and analytics data</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Location information (if permitted by your device settings)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">2.3 Data Processed Through Our Platform</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              As a data infrastructure platform, we process data on behalf of our clients ("Customer Data"). Customer Data remains the property of our clients, and we process it solely in accordance with our clients' instructions and our Data Processing Agreement. We do not use Customer Data for any purpose other than providing our Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our Service, including processing transactions, managing accounts, and delivering features</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries, provide technical support, and address service-related issues</li>
              <li><strong>Communication:</strong> To send you service-related notifications, updates, and administrative messages</li>
              <li><strong>Marketing:</strong> To send you promotional materials, newsletters, and other marketing communications (with your consent, where required)</li>
              <li><strong>Analytics:</strong> To analyze usage patterns, improve our Service, and develop new features</li>
              <li><strong>Security:</strong> To detect, prevent, and address security threats, fraud, and unauthorized access</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Business Operations:</strong> To manage our business operations, including billing, accounting, and internal reporting</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">4.1 Service Providers</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may share your information with trusted third-party service providers who assist us in operating our Service, conducting our business, or serving our users. These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">4.2 Business Transfers</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control of your personal information.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">4.3 Legal Requirements</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, or government investigations).
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">4.4 With Your Consent</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may share your information with third parties when you have given us explicit consent to do so.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We implement industry-standard technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and vulnerability testing</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure data centers with physical and logical security controls</li>
              <li>Employee training on data protection and security best practices</li>
              <li>Incident response procedures and breach notification protocols</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it in accordance with our data retention policies.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Customer Data processed through our platform is retained in accordance with our Data Processing Agreement and our clients' instructions. Upon termination of a client's account, we will delete or return Customer Data as specified in our agreement.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights and Choices</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li><strong>Access:</strong> The right to request access to your personal information</li>
              <li><strong>Correction:</strong> The right to request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> The right to request deletion of your personal information</li>
              <li><strong>Portability:</strong> The right to receive your personal information in a structured, commonly used format</li>
              <li><strong>Objection:</strong> The right to object to processing of your personal information</li>
              <li><strong>Restriction:</strong> The right to request restriction of processing</li>
              <li><strong>Withdraw Consent:</strong> The right to withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-4">
              To exercise these rights, please contact us using the information provided in the "Contact Us" section below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
            </p>
            <p className="text-slate-700 leading-relaxed">
              You may also opt out of marketing communications by clicking the unsubscribe link in our emails or by contacting us directly.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use cookies, web beacons, and similar tracking technologies to collect and store information about your use of our Service. These technologies help us:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Analyze how you use our Service</li>
              <li>Provide personalized content and advertisements</li>
              <li>Improve security and prevent fraud</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-4">
              You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our Service.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We may use third-party analytics services (such as Google Analytics) that use cookies and similar technologies to analyze usage patterns. These services may collect information about your use of our Service and other websites.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country.
            </p>
            <p className="text-slate-700 leading-relaxed">
              When we transfer your information internationally, we implement appropriate safeguards, such as standard contractual clauses approved by relevant data protection authorities, to ensure your information receives an adequate level of protection.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete such information promptly.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. California Privacy Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>The right to know what personal information we collect, use, and disclose</li>
              <li>The right to delete your personal information</li>
              <li>The right to opt out of the sale of personal information (we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              To exercise your California privacy rights, please contact us using the information provided in the "Contact Us" section below.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. European Privacy Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional rights under the General Data Protection Regulation (GDPR), including those listed in Section 7 above.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Our legal basis for processing your personal information includes:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Performance of a contract with you</li>
              <li>Your consent</li>
              <li>Compliance with legal obligations</li>
              <li>Our legitimate business interests</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              You have the right to lodge a complaint with your local data protection authority if you believe we have violated your privacy rights.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4 ml-4">
              <li>Posting the updated Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date at the top of this Privacy Policy</li>
              <li>Sending you an email notification (for significant changes)</li>
              <li>Displaying a prominent notice on our Service</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              Your continued use of our Service after any changes to this Privacy Policy constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4">
              <p className="text-slate-900 font-semibold mb-2">KMG</p>
              <p className="text-slate-700 mb-1">
                <strong>Email:</strong> info@kmgtechnologies.com
              </p>
              <p className="text-slate-700 mb-1">
                <strong>Address:</strong> [Your Company Address]
              </p>
              <p className="text-slate-700">
                <strong>Data Protection Officer:</strong> info@kmgtechnologies.com
              </p>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
